import {
  Box,
  Icon,
  Image,
  Spinner,
  Text,
  VStack,
  Button,
  Badge,
  Heading,
  Center,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { WhatsAppSessionStatusResponse } from '@/services/interface';
import { RiWhatsappLine, RiCheckboxCircleLine } from 'react-icons/ri';
import { FiRefreshCw } from 'react-icons/fi';

interface ChannelConnectionProps {
  isCreatingSession: boolean;
  isConnected: boolean;
  isReconnecting?: boolean;
  onConnectionSuccess?: () => void;
  qrCodeData?: string;
  isLoadingQR?: boolean;
  onRefreshQR?: () => void;
  sessionStatus?: WhatsAppSessionStatusResponse | null;
}

export function ChannelConnection({
  isCreatingSession,
  isConnected,
  isReconnecting = false,
  onConnectionSuccess,
  qrCodeData,
  isLoadingQR = false,
  onRefreshQR,
  sessionStatus,
}: ChannelConnectionProps) {
  const [retryCount, setRetryCount] = useState(0);

  // Auto-refresh QR when needed
  useEffect(() => {
    if (!isConnected && !isLoadingQR && onRefreshQR) {
      // Initial load
      if (retryCount === 0) {
        onRefreshQR();
        setRetryCount(1);
      }

      // Set up periodic refresh if we need a QR code
      const needQrCode = sessionStatus?.status === 'SCAN_QR_CODE';
      if (needQrCode) {
        const refreshTimer = setTimeout(() => {
          onRefreshQR();
          setRetryCount((prev) => prev + 1);
        }, 20000); // Refresh every 20 seconds if not loaded

        return () => clearTimeout(refreshTimer);
      }
    }
  }, [isConnected, isLoadingQR, onRefreshQR, retryCount, sessionStatus]);

  // Monitor connection status
  useEffect(() => {
    if (isConnected && onConnectionSuccess) {
      onConnectionSuccess();
    }
  }, [isConnected, onConnectionSuccess]);

  // Render appropriate UI based on session status
  return (
    <Box>
      <VStack gap={6} align="center" w="full">
        <Icon as={RiWhatsappLine} boxSize={16} color="green.500" />

        <Heading size="md">
          {isReconnecting
            ? 'Reconnect Your WhatsApp Account'
            : 'Connect Your WhatsApp Account'}
        </Heading>

        <Text textAlign="center" color="gray.600">
          {isReconnecting
            ? 'To reconnect WhatsApp, follow the instructions below'
            : 'To use WhatsApp with our platform, you need to scan the QR code'}
        </Text>

        {isCreatingSession ? (
          <Spinner size="xl" color="green.500" mt={8} />
        ) : isConnected ? (
          <VStack gap={4}>
            <Icon as={RiCheckboxCircleLine} boxSize={20} color="green.500" />
            <Text fontWeight="bold" fontSize="lg">
              WhatsApp Connected Successfully!
            </Text>
          </VStack>
        ) : (
          <Box
            border="1px"
            borderColor="gray.200"
            p={6}
            borderRadius="md"
            w="full"
            maxW="320px"
            bg="white"
          >
            {isLoadingQR || !qrCodeData ? (
              <Center flexDir="column" py={8}>
                <Spinner size="lg" mb={4} />
                <Text>Checking connection status...</Text>
              </Center>
            ) : (
              <VStack gap={4}>
                <Box>
                  <Image
                    src={qrCodeData}
                    alt="WhatsApp QR Code"
                    boxSize="280px"
                  />
                </Box>
                <Text fontSize="sm" color="gray.600" textAlign="center">
                  Scan this QR code with your WhatsApp app to connect
                </Text>
                <Button variant="outline" size="sm" onClick={onRefreshQR}>
                  <FiRefreshCw />
                  Refresh QR Code
                </Button>
              </VStack>
            )}
          </Box>
        )}

        {sessionStatus && (
          <Box
            mt={2}
            p={2}
            bg="gray.50"
            borderRadius="md"
            w="full"
            maxW="350px"
          >
            <Text fontWeight="semibold" fontSize="sm">
              Status:{' '}
              <Badge colorScheme={getStatusColor(sessionStatus.status)}>
                {sessionStatus.status}
              </Badge>
            </Text>
            {sessionStatus.status === 'FAILED' && (
              <Text color="red.500" fontSize="sm" mt={1}>
                Connection failed. Please try refreshing the QR code.
              </Text>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  );
}

// Helper function to get status color
function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    WORKING: 'green',
    CONNECTED: 'green',
    PENDING: 'yellow',
    FAILED: 'red',
    STARTING: 'blue',
    STOPPED: 'gray',
    SCAN_QR_CODE: 'purple',
    STOPPING: 'orange',
    INITIALIZING: 'blue',
  };
  return statusColors[status] || 'gray';
}
